/* eslint-disable strict */
/* eslint-disable no-param-reassign */
((algolia) => {
  'use strict';

  const { rangeSlider, menu, refinementList } = algolia.externals.widgets;

  const TYPES_TO_WIDGET = {
    slider: { name: 'rangeSlider', useDefault: true, widget: rangeSlider },
    menu: { name: 'menu', params: { limit: 10 }, widget: menu },
    conjunctive: {
      name: 'refinementList',
      params: { operator: 'and', limit: 10, showMore: true, showMoreLimit: 50 },
      widget: refinementList,
    },
    disjunctive: {
      name: 'refinementList',
      params: { operator: 'or', limit: 10, showMore: true, showMoreLimit: 50 },
      widget: refinementList,
    },
  };

  const sortByRefined = function sortByRefined(sortFunction) {
    return (a, b) => {
      if (a.refined !== b.refined) {
        if (a.refined) {
          return -1;
        }
        if (b.refined) {
          return 1;
        }
      }
      return sortFunction(a, b);
    };
  };

  /*
   * Sorting functions : Allows to chose in which order you want to display the facets
   * Algolia will always send you back the most relevant values for each facet (the ones
   * with the highest count). These sorting functions won't change which results come back
   * but how they are displayed. To retrieve more results, change the maxValuesPerFacet
   * parameter in your Algolia Dashboard.
   * The default sort function is refined > count > name.
   */
  algolia.facetSortFunctions = {
    price_range: sortByRefined((a, b) => {
      if (a.name.length === b.name.length) {
        return a.name.localeCompare(b.name);
      }
      return a.name.length - b.name.length;
    }),
  };

  /*
   * Display functions
   * When the object sent back for a facet item doesn't match how you would want it to look
   * like, use a function to reformat it how you want.
   */
  algolia.facetDisplayFunctions = {
    price_range: function displayRange(value) {
      const values = value.split(':');

      return values
        .map(e => algolia.formatMoney(Number(e) * 100).replace(/\.\d+/, ''))
        .join(' - ');
    },
    collections: (value) => {
      const values = value.split('-');

      return values
        .map((string) => string.charAt(0).toUpperCase() + string.slice(1))
        .join(' ');
    }
  };

  algolia.facetCssClasses = {
    noRefinementRoot: 'foo',
    list: ['filter-group__facet-list', 'list-reset'],
    item: 'filter-group__facet',
    selectedItem: 'filter-group__facet--selected',
    label: 'filter-group__label',
    checkbox: ['filter-group__checkbox', 'checkbox'],
    count: 'filter-group__count',
    searchableRoot: 'filter-group__search',
    searchableForm: 'filter-group__search-form',
    searchableInput: ['filter-group__search-input', 'text-box'],
    searchableSubmit: ['filter-group__search-submit', 'button-reset'],
    searchableSubmitIcon: 'filter-group__search-icon',
    searchableReset: 'filter-group__search-reset',
    searchableResetIcon: 'filter-group__search-reset-icon',
    searchableLoadingIndicator: 'filter-group__search-loading',
    searchableLoadingIcon: 'filter-group__search-loading-icon',
    showMore: ['filter-group__view-toggle', 'button-reset']
  };

  const enabledFacets = algolia.config.facets.filter(facet => facet.enabled || parseInt(facet.enabled, 10));

  algolia.facets = enabledFacets.map(facet => ({
    ...facet,
    escapedName: encodeURIComponent(facet.name),
  }));
  algolia.shownFacets = algolia.facets.filter(facet => facet.type !== 'hidden');
  algolia.hiddenFacets = algolia.config.facets.filter(facet => facet.type === 'hidden');

  const facetToWidget = (facet) => {
    const widget = TYPES_TO_WIDGET[facet.type];
    const params = algolia.assign({}, widget.params);

    params.container = `[data-filter-group-facets="${facet.escapedName}"]`;
    params.attribute = facet.name;
    params.templates = {};
    params.cssClasses = algolia.facetCssClasses;

    /**
     * Providing a custom "Show more" template to confirm to the custom
     */
    params.templates.showMoreText = function showMoreText(data, { html }) {
      return algolia.instantSearchShowMore(data, html)
    }

    if (facet.searchable) {
      params.searchable = true;
      params.searchablePlaceholder = `${WYATT.t.filters.searchFor} ${facet.title}`;
      params.templates.searchableNoResults = `<p>${WYATT.t.filters.noMatchesFor} ${facet.title}</p>`;
    }

    params.templates.header = () => facet.title;

    if (!widget.useDefault) {
      params.templates.item = (item, { html }) => algolia.instantSearchFacetItem(item, html)
    }

    if (algolia.facetSortFunctions[facet.name]) {
      params.sortBy = algolia.facetSortFunctions[facet.name];
    }

    const displayFunction = algolia.facetDisplayFunctions[facet.name];

    params.transformItems = (items) => items.map(item => ({
        ...item,
        type: facet.type,
        label: displayFunction ? displayFunction(item.value) : item.value
      }))

    return {
      name: widget.name,
      params,
      widget: widget.widget,
    };
  };

  // try to fetch facets for current collection or fallback to collections default
  const collectionFacets =
    algolia.current_collection_id &&
    algolia.config.collection_facets &&
    algolia.config.collection_facets[algolia.current_collection_id]
      ? algolia.config.collection_facets[algolia.current_collection_id]
      : algolia.config.collection_facets &&
        algolia.config.collection_facets.default;

  if (collectionFacets) {
    const enabledCollectionFacets = collectionFacets.filter(facet => facet.enabled || parseInt(facet.enabled, 10));

    algolia.collectionFacets = enabledCollectionFacets.map(facet => ({
      ...facet,
      escapedName: encodeURIComponent(facet.name)
    }));
    algolia.collectionShownFacets = algolia.collectionFacets.filter(facet => facet.type !== 'hidden');
    algolia.collectionHiddenFacets = collectionFacets.filter(facet => facet.type === 'hidden');
    algolia.collectionFacetsWidgets = algolia.collectionShownFacets.map(facetToWidget);
  }
  algolia.facetsWidgets = algolia.shownFacets.map(facetToWidget);
})(window.algoliaShopify);
